import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Lock, Mail, User, ScanLine, KeyRound } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { setSession } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [step, setStep] = useState("credentials"); // credentials | setup | verify
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await api.post("/auth/register", { email, password, name });
        toast.success("Account created. Please sign in.");
        setMode("login");
        setLoading(false);
        return;
      }
      const { data } = await api.post("/auth/login", { email, password });
      setTempToken(data.temp_token);
      if (data.requires_2fa_setup) {
        const setup = await api.post("/auth/2fa/setup", { temp_token: data.temp_token });
        setQr(setup.data.qr_code);
        setSecret(setup.data.secret);
        setStep("setup");
      } else {
        setStep("verify");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = step === "setup" ? "/auth/2fa/enable" : "/auth/2fa/verify";
      const { data } = await api.post(endpoint, { temp_token: tempToken, code });
      if (step === "setup" && data.backup_codes) {
        setBackupCodes(data.backup_codes);
        setPendingSession({ token: data.access_token, user: data.user });
        setStep("backup");
      } else {
        setSession(data.access_token, data.user);
        toast.success(`Welcome back, ${data.user.name}`);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const finishBackup = () => {
    if (pendingSession) {
      setSession(pendingSession.token, pendingSession.user);
      toast.success(`Welcome, ${pendingSession.user.name}`);
    }
  };

  return (
    <div className="min-h-screen grid-bg bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/20 via-transparent to-transparent pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-xl bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight leading-none">CITIDAN</h1>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">Financial Datacenter</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card backdrop-blur-xl p-7 shadow-2xl">
          {step === "credentials" && (
            <form onSubmit={submitCredentials} className="space-y-5" data-testid="login-form">
              <div>
                <h2 className="font-display text-xl font-semibold">
                  {mode === "login" ? "Sign in to your account" : "Create an account"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {mode === "login" ? "Protected by two-factor authentication" : "You'll set up 2FA on first sign in"}
                </p>
              </div>

              {mode === "register" && (
                <Field icon={User} label="Full Name">
                  <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Operator" className="pl-10 bg-muted/40 border-border" required />
                </Field>
              )}
              <Field icon={Mail} label="Email">
                <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@citidan.io" className="pl-10 bg-muted/40 border-border" required />
              </Field>
              <Field icon={Lock} label="Password">
                <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-10 bg-muted/40 border-border" required />
              </Field>

              <Button data-testid="login-submit-button" type="submit" disabled={loading}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Continue" : "Create account"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                {mode === "login" ? "Need an account?" : "Already registered?"}{" "}
                <button type="button" data-testid="toggle-auth-mode"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-indigo-400 hover:text-indigo-300 font-medium">
                  {mode === "login" ? "Register" : "Sign in"}
                </button>
              </p>
            </form>
          )}

          {(step === "setup" || step === "verify") && (
            <form onSubmit={submitCode} className="space-y-5" data-testid="twofa-form">
              <div className="flex items-center gap-2">
                {step === "setup" ? <ScanLine className="h-5 w-5 text-indigo-400" /> : <KeyRound className="h-5 w-5 text-indigo-400" />}
                <h2 className="font-display text-xl font-semibold">
                  {step === "setup" ? "Set up two-factor auth" : "Two-factor verification"}
                </h2>
              </div>

              {step === "setup" && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Scan this QR code with Google Authenticator, Authy or 1Password, then enter the 6-digit code.
                  </p>
                  {qr && (
                    <div className="flex justify-center">
                      <img src={qr} alt="2FA QR code" data-testid="twofa-qr" className="h-44 w-44 rounded-lg border border-border bg-white p-2" />
                    </div>
                  )}
                  <div className="rounded-lg bg-muted/40 border border-border p-3">
                    <p className="text-xs text-muted-foreground mb-1">Manual setup key</p>
                    <code data-testid="twofa-secret" className="text-xs text-indigo-300 break-all">{secret}</code>
                  </div>
                </div>
              )}

              <Field label="6-digit code">
                <Input data-testid="totp-input-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" placeholder="000000"
                  className="text-center text-2xl tracking-[0.5em] font-mono bg-muted/40 border-border" required />
              </Field>

              <Button data-testid="2fa-verify-button" type="submit" disabled={loading || code.length !== 6}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : step === "setup" ? "Enable & sign in" : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setStep("credentials"); setCode(""); }}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground" data-testid="twofa-back">
                Back
              </button>
            </form>
          )}

          {step === "backup" && backupCodes && (
            <div className="space-y-5" data-testid="backup-codes-panel">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-emerald-400" />
                <h2 className="font-display text-xl font-semibold">Save your backup codes</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Store these one-time codes somewhere safe. Each can be used once to sign in if you lose
                your authenticator. They won't be shown again.
              </p>
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 border border-border p-4">
                {backupCodes.map((c) => (
                  <code key={c} data-testid="backup-code" className="text-sm text-indigo-300 font-mono text-center">{c}</code>
                ))}
              </div>
              <Button data-testid="copy-backup-codes"
                variant="outline" onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Backup codes copied"); }}
                className="w-full border-border bg-card text-foreground hover:bg-accent">
                Copy all codes
              </Button>
              <Button data-testid="backup-continue-button" onClick={finishBackup}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium">
                I've saved them — continue
              </Button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6 font-mono">CITIDAN · encrypted · double-entry ledger</p>
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />}
        {children}
      </div>
    </div>
  );
}
